<script setup lang="ts">
	import { computed, ref } from 'vue'

	import { useHotelReportReservations } from '../composables/useHotelReportReservations'

	import SummaryKpis from './charts/SummaryKpis.vue'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		datePreset?: string
		creationDatePreset?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = withDefaults(defineProps<Props>(), {
		statusFilter: 'all',
		searchTravelerId: null,
		searchHotelId: null,
	})

	const currentPage = ref(1)
	const pageSize = ref(10)

	const { requests, meta, isLoading } = useHotelReportReservations(
		computed(() => ({
			companyId: props.companyId,
			page: currentPage.value,
			perPage: pageSize.value,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	const formatCurrency = (value: string | number) => {
		const numValue = typeof value === 'string' ? parseFloat(value) : value
		return new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
			signDisplay: 'never',
		}).format(numValue)
	}

	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString('es-MX', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		})
	}

	const getStateBadgeClass = (stateName: string) => {
		const stateClasses: Record<string, string> = {
			Confirmado: 'bg-emerald-50 text-emerald-700',
			Pendiente: 'bg-amber-50 text-amber-700',
			'Pendiente Pago': 'bg-amber-50 text-amber-700',
			Cancelado: 'bg-red-50 text-red-700',
			Pagado: 'bg-blue-50 text-blue-700',
		}

		return stateClasses[stateName] || 'bg-gray-100 text-gray-600'
	}

	const goToPage = (page: number) => {
		currentPage.value = page
	}

	// Control de tooltips para móvil
	const activeTooltip = ref<string | null>(null)

	const toggleTooltip = (id: string) => {
		activeTooltip.value = activeTooltip.value === id ? null : id
	}

	const closeTooltip = () => {
		activeTooltip.value = null
	}
</script>

<template>
	<div class="w-full space-y-6">
		<!-- Resumen General - Versión compacta -->
		<div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
			<h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Resumen</h3>
			<SummaryKpis
				:company-id="companyId"
				:date-range="dateRange"
				:creation-date-range="creationDateRange"
				:status-filter="statusFilter"
				:search-traveler-id="searchTravelerId"
				:search-hotel-id="searchHotelId"
				:created-at="createdAt"
				compact
			/>
		</div>

		<!-- Vista móvil (cards) -->
		<div class="lg:hidden space-y-3">
			<!-- Loading skeleton móvil -->
			<div
				v-if="isLoading"
				v-for="n in 4"
				:key="n"
				class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 animate-pulse"
			>
				<div class="flex items-center justify-between mb-3">
					<div class="flex items-center gap-2">
						<div class="h-5 w-5 rounded-full bg-gray-200"></div>
						<div class="space-y-1.5">
							<div class="h-3 w-28 rounded bg-gray-200"></div>
							<div class="h-2.5 w-20 rounded bg-gray-100"></div>
						</div>
					</div>
					<div class="h-5 w-16 rounded-full bg-gray-200"></div>
				</div>
				<div class="border-t border-gray-100 pt-3 space-y-2">
					<div class="flex justify-between">
						<div class="h-2.5 w-12 rounded bg-gray-100"></div>
						<div class="h-2.5 w-20 rounded bg-gray-200"></div>
					</div>
					<div class="flex justify-between">
						<div class="h-2.5 w-14 rounded bg-gray-100"></div>
						<div class="h-2.5 w-24 rounded bg-gray-200"></div>
					</div>
					<div class="flex justify-between">
						<div class="h-2.5 w-10 rounded bg-gray-100"></div>
						<div class="h-2.5 w-16 rounded bg-gray-200"></div>
					</div>
				</div>
			</div>

			<!-- Empty state móvil -->
			<div
				v-else-if="requests.length === 0"
				class="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 flex flex-col items-center"
			>
				<span class="material-symbols-outlined text-gray-300 text-5xl mb-3">inbox</span>
				<p class="text-gray-500 text-base font-medium text-center">No hay solicitudes disponibles</p>
				<p class="text-gray-400 text-sm mt-1 text-center">Ajusta los filtros para ver más resultados</p>
			</div>

			<!-- Cards móvil -->
			<div
				v-else
				v-for="request in requests"
				:key="request.id"
				class="rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white"
			>
				<div class="p-4 space-y-3">
					<!-- Header con estado -->
					<div class="flex items-start justify-between">
						<div class="flex items-center gap-2 flex-1 min-w-0">
							<div class="inline-flex items-center gap-1">
								<span
									v-if="request.isPreliminary"
									class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 border border-amber-200"
								>
									<span class="material-symbols-outlined text-amber-600 text-xs">schedule</span>
								</span>
								<span
									v-if="(request.savingsInBaseCurrency ?? 0) > 0"
									class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 border border-emerald-200"
								>
									<span class="material-symbols-outlined text-emerald-600 text-xs">savings</span>
								</span>
							</div>
							<div class="min-w-0">
								<p class="text-sm font-semibold text-gray-900 truncate">{{ request.travelerName }}</p>
								<p class="text-xs text-gray-500 mt-0.5 capitalize truncate">{{ request.suppliableName }}</p>
							</div>
						</div>
						<span
							:class="getStateBadgeClass(request.stateName)"
							class="px-2.5 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ml-2"
						>
							{{ request.stateName }}
						</span>
					</div>

					<!-- Información principal -->
					<div class="space-y-2 border-t border-gray-100 pt-3">
						<div class="flex items-center justify-between text-sm">
							<span class="text-gray-400 text-xs">Ciudad</span>
							<span class="text-gray-800 font-medium text-xs">{{ request.suppliable?.city }}</span>
						</div>
						<div class="flex items-center justify-between text-sm">
							<span class="text-gray-400 text-xs">Check-in</span>
							<span class="text-gray-800 font-medium text-xs">{{ formatDate(request.startDate) }}</span>
						</div>
						<div class="flex items-center justify-between text-sm">
							<span class="text-gray-400 text-xs">Check-out</span>
							<span class="text-gray-800 font-medium text-xs">{{ formatDate(request.endDate) }}</span>
						</div>
						<div class="flex items-center justify-between pt-1">
							<span class="text-gray-400 text-xs">Total</span>
							<span class="text-gray-900 font-bold text-sm tabular-nums">
								{{ formatCurrency(request.total) }}
								<span class="text-[10px] font-normal text-gray-400">MXN</span>
							</span>
						</div>
					</div>

					<!-- Footer con metadata -->
					<div class="border-t border-gray-100 pt-3 space-y-2">
						<div class="flex items-center justify-between">
							<span class="text-gray-400 text-xs">Creado</span>
							<span class="text-gray-600 text-xs">
								{{ formatDate(request.createdAt) }}
								{{ new Date(request.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) }}
							</span>
						</div>
						<div class="flex items-center justify-between">
							<span class="text-gray-400 text-xs">Solicitado por</span>
							<span class="text-gray-600 text-xs truncate max-w-[150px]">{{ request.memberName }}</span>
						</div>
						<div class="flex items-center justify-between">
							<span class="text-gray-400 text-xs">Reservado por</span>
							<div class="flex items-center gap-1.5">
								<img v-if="request.creator.internal" src="@/assets/imgs/icono_enlace.png" class="!w-4" alt="" />
								<span class="text-gray-600 text-xs truncate max-w-[150px]">
									{{ request.creator.name }} {{ request.creator.lastname }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Vista tablet/desktop (tabla) -->
		<div class="hidden lg:block rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
			<div class="overflow-x-auto">
			<table class="w-full table-fixed bg-white">
				<thead>
					<tr class="bg-gray-50 border-b border-gray-200">
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[11%]">Viajero</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[13%]">Hotel</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[9%]">Ciudad</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[9%]">Fecha Inicio</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[9%]">Fecha Fin</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[9%]">Total</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[8%]">Estatus</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[10%]">Fecha Creación</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[10%]">Solicitado por</th>
						<th class="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[10%]">Reservado por</th>
					</tr>
				</thead>
				<tbody class="divide-y divide-gray-100">
					<!-- Loading skeleton rows -->
					<tr
						v-if="isLoading"
						v-for="n in 8"
						:key="n"
						class="animate-pulse"
					>
						<td class="px-4 py-3.5">
							<div class="flex items-center gap-1.5">
								<div class="h-4 w-4 rounded-full bg-gray-200 shrink-0"></div>
								<div class="h-3 rounded bg-gray-200 w-3/4"></div>
							</div>
						</td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-4/5"></div></td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-2/3"></div></td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-3/4"></div></td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-3/4"></div></td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-2/3"></div></td>
						<td class="px-4 py-3.5"><div class="h-5 rounded-full bg-gray-200 w-16"></div></td>
						<td class="px-4 py-3.5">
							<div class="space-y-1.5">
								<div class="h-3 rounded bg-gray-200 w-4/5"></div>
								<div class="h-2.5 rounded bg-gray-100 w-1/2"></div>
							</div>
						</td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-3/4"></div></td>
						<td class="px-4 py-3.5"><div class="h-3 rounded bg-gray-200 w-3/4"></div></td>
					</tr>

					<!-- Empty state -->
					<tr v-else-if="requests.length === 0">
						<td colspan="10" class="px-4 py-16 text-center">
							<div class="flex flex-col items-center">
								<span class="material-symbols-outlined text-gray-300 text-5xl mb-3">inbox</span>
								<p class="text-gray-500 text-base font-medium">No hay solicitudes disponibles</p>
								<p class="text-gray-400 text-sm mt-1">Ajusta los filtros para ver más resultados</p>
							</div>
						</td>
					</tr>

					<!-- Data rows -->
					<tr
						v-else
						v-for="(request, i) in requests"
						:key="request.id"
						:class="i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'"
						class="hover:bg-blue-50/30 transition-colors"
					>
						<td class="px-4 py-3.5 text-sm text-gray-900">
							<div class="flex items-center gap-1 mb-1">
								<span
									v-if="request.isPreliminary"
									v-tooltip="'Reserva preliminar: la tarifa puede cambiar'"
									class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 border border-amber-200 cursor-help hover:shadow-sm transition-shadow"
								>
									<span class="material-symbols-outlined text-amber-600 text-xs">schedule</span>
								</span>
								<span
									v-if="(request.savingsInBaseCurrency ?? 0) > 0"
									v-tooltip="`Ahorro: ${formatCurrency(request.savingsInBaseCurrency ?? 0)}`"
									class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 border border-emerald-200 cursor-help hover:shadow-sm transition-shadow"
								>
									<span class="material-symbols-outlined text-emerald-600 text-xs">savings</span>
								</span>
							</div>
							<span v-tooltip="request.travelerName" class="block truncate text-gray-800 font-medium">
								{{ request.travelerName }}
							</span>
						</td>
						<td class="px-4 py-3.5 text-sm capitalize">
							<span v-tooltip="request.suppliableName" class="block truncate text-gray-700 font-medium">
								{{ request.suppliableName }}
							</span>
						</td>
						<td class="px-4 py-3.5 text-sm text-gray-600">
							<span v-tooltip="request.suppliable?.city" class="block truncate">{{ request.suppliable?.city }}</span>
						</td>
						<td class="px-4 py-3.5 text-sm text-gray-600 tabular-nums">{{ formatDate(request.startDate) }}</td>
						<td class="px-4 py-3.5 text-sm text-gray-600 tabular-nums">{{ formatDate(request.endDate) }}</td>
						<td class="px-4 py-3.5 text-sm font-semibold text-gray-900 tabular-nums">
							{{ formatCurrency(request.total) }}
							<span class="text-[10px] font-normal text-gray-400">MXN</span>
						</td>
						<td class="px-4 py-3.5">
							<span
								:class="getStateBadgeClass(request.stateName)"
								class="px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ring-1 ring-inset ring-current/20"
							>
								{{ request.stateName }}
							</span>
						</td>
						<td class="px-4 py-3.5 text-xs text-gray-600">
							<div class="flex flex-col gap-0.5">
								<span class="font-medium text-gray-800">{{ formatDate(request.createdAt) }}</span>
								<span class="text-gray-400">
									{{ new Date(request.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) }}
								</span>
							</div>
						</td>
						<td class="px-4 py-3.5 text-sm text-gray-600">
							<span v-tooltip="request.memberName" class="block truncate">{{ request.memberName }}</span>
						</td>
						<td class="px-4 py-3.5 text-sm text-gray-600">
							<div class="flex items-center gap-1.5">
								<img v-if="request.creator.internal" src="@/assets/imgs/icono_enlace.png" class="!w-5 shrink-0" alt="" />
								<p
									v-tooltip="`${request.creator.name} ${request.creator.lastname}`"
									class="truncate"
								>
									{{ request.creator.name }} {{ request.creator.lastname }}
								</p>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
			</div>
		</div>

		<!-- Paginación -->
		<div
			v-if="meta && meta.total > 0"
			class="flex items-center justify-between bg-white px-5 py-4 rounded-2xl border border-gray-100 shadow-sm"
		>
			<!-- Móvil -->
			<div class="sm:hidden w-full">
				<div class="flex flex-col items-center gap-3 mb-4">
					<p class="text-sm text-gray-600 text-center">
						<span class="font-semibold text-gray-900">{{ (currentPage - 1) * pageSize + 1 }}</span>
						–
						<span class="font-semibold text-gray-900">{{ Math.min(currentPage * pageSize, meta.total) }}</span>
						de
						<span class="font-semibold text-gray-900">{{ meta.total }}</span>
					</p>
					<div class="flex items-center gap-2">
						<label for="perPageMobile" class="text-sm text-gray-500">Por página:</label>
						<select
							id="perPageMobile"
							v-model="pageSize"
							@change="currentPage = 1"
							class="rounded-lg border border-gray-200 shadow-sm text-sm px-2 py-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
						>
							<option :value="5">5</option>
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>
				<div class="flex justify-center gap-2">
					<button
						@click="goToPage(currentPage - 1)"
						:disabled="currentPage === 1"
						class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
					>
						Anterior
					</button>
					<button
						@click="goToPage(currentPage + 1)"
						:disabled="currentPage === meta.lastPage"
						class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
					>
						Siguiente
					</button>
				</div>
			</div>

			<!-- Desktop -->
			<div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<div class="flex items-center gap-4">
					<p class="text-sm text-gray-600">
						Mostrando
						<span class="font-semibold text-gray-900 tabular-nums">{{ (currentPage - 1) * pageSize + 1 }}</span>
						–
						<span class="font-semibold text-gray-900 tabular-nums">{{ Math.min(currentPage * pageSize, meta.total) }}</span>
						de
						<span class="font-semibold text-gray-900 tabular-nums">{{ meta.total }}</span>
						solicitudes
					</p>
					<div class="flex items-center gap-2">
						<label for="perPage" class="text-sm text-gray-500">Por página:</label>
						<select
							id="perPage"
							v-model="pageSize"
							@change="currentPage = 1"
							class="rounded-lg border border-gray-200 shadow-sm text-sm px-2 py-1 focus:border-[#021F58] focus:ring-1 focus:ring-[#021F58]"
						>
							<option :value="5">5</option>
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>
				<nav class="inline-flex items-center gap-1" aria-label="Pagination">
					<button
						@click="goToPage(currentPage - 1)"
						:disabled="currentPage === 1"
						class="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
					>
						<span class="sr-only">Anterior</span>
						<span class="material-symbols-outlined text-base">chevron_left</span>
					</button>

					<template v-for="page in meta.lastPage" :key="page">
						<button
							v-if="page === 1 || page === meta.lastPage || (page >= currentPage - 2 && page <= currentPage + 2)"
							@click="goToPage(page)"
							:class="[
								page === currentPage
									? 'bg-[#021F58] text-white border-[#021F58] shadow-sm'
									: 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
								'inline-flex items-center justify-center w-9 h-9 rounded-xl border text-sm font-semibold transition-colors',
							]"
						>
							{{ page }}
						</button>
						<span
							v-else-if="page === currentPage - 3 || (page === currentPage + 3 && page < meta.lastPage)"
							class="inline-flex items-center justify-center w-9 h-9 text-sm text-gray-400"
						>
							···
						</span>
					</template>

					<button
						@click="goToPage(currentPage + 1)"
						:disabled="currentPage === meta.lastPage"
						class="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
					>
						<span class="sr-only">Siguiente</span>
						<span class="material-symbols-outlined text-base">chevron_right</span>
					</button>
				</nav>
			</div>
		</div>
	</div>
</template>
